import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, ShoppingBag, ShieldCheck, ExternalLink, Ticket, Gift } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PRODUCT_IMG, AMAZON_URL } from "@/lib/constants";
import { useLang } from "@/i18n/LanguageContext";

export function ProductSection() {
  const { t } = useLang();
  const [loading, setLoading] = useState(false);
  const [discount, setDiscount] = useState(null);

  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get("code");
    if (!code) return;
    api.get(`/discount/${encodeURIComponent(code)}`)
      .then(({ data }) => { if (data.valid) setDiscount(data); })
      .catch(() => {});
  }, []);

  const buy = async () => {
    setLoading(true);
    try {
      const payload = { origin_url: window.location.origin };
      if (discount) payload.discount_code = discount.code;
      const { data } = await api.post("/checkout/session", payload);
      if (data.url) window.location.href = data.url;
      else throw new Error("no url");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore checkout");
      setLoading(false);
    }
  };

  return (
    <section id="prodotto" data-testid="product-section" className="relative z-10 bg-[#0a0a0a] py-24 md:py-36 border-y border-white/5">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        <Reveal>
          <div className="relative rounded-2xl overflow-hidden border border-aura-border aspect-square bg-aura-surface">
            <img src={PRODUCT_IMG} alt={t("product.name")} className="w-full h-full object-cover" />
            <div className="absolute inset-0 spotlight pointer-events-none" />
            <span className="absolute top-5 start-5 rounded-full bg-white text-black text-xs font-bold px-4 py-1.5">Meta Quest 3</span>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{t("product.overline")}</span>
          <h2 className="font-display font-bold tracking-[-0.02em] text-white text-4xl md:text-5xl lg:text-6xl mt-5 leading-[1.02]">
            {t("product.heading")}
          </h2>
          <p className="mt-7 text-lg text-zinc-400 leading-relaxed max-w-lg">{t("product.desc")}</p>

          <div className="mt-8 flex items-baseline gap-3">
            {discount ? (
              <>
                <span data-testid="product-price" className="font-display text-4xl font-bold text-white">€{discount.price.toFixed(2).replace(".", ",")}</span>
                <span className="text-lg text-zinc-500 line-through">{t("product.price")}</span>
                <span data-testid="discount-badge" className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-300">
                  <Ticket size={13} /> {discount.code} · -{discount.percent}%
                </span>
              </>
            ) : (
              <button type="button" onClick={() => document.getElementById("apply")?.scrollIntoView({ behavior: "smooth" })}
                data-testid="free-cta" className="inline-flex items-center gap-2 text-lg font-semibold text-emerald-300 hover:text-emerald-200 transition-colors">
                <Gift size={18} /> {t("product.freeCta")}
              </button>
            )}
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <button
              data-testid="buy-stripe-btn"
              onClick={buy}
              disabled={loading}
              className="group rounded-full bg-white text-black font-semibold text-base px-8 py-4 hover:bg-zinc-200 active:scale-95 transition-[background-color,transform] duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <ShoppingBag size={18} />}
              {t("product.buyStripe")}
            </button>
            <a
              data-testid="buy-amazon-btn"
              href={AMAZON_URL}
              target="_blank"
              rel="noreferrer"
              className="group rounded-full border border-white/20 text-white font-semibold text-base px-8 py-4 hover:border-white/50 hover:bg-white/5 transition-colors duration-300 flex items-center justify-center gap-2"
            >
              {t("product.buyAmazon")}
              <ExternalLink size={16} className="opacity-70 group-hover:opacity-100 transition-opacity" />
            </a>
          </div>

          <div className="mt-6 flex items-center gap-2 text-xs text-zinc-500">
            <ShieldCheck size={14} /> {t("product.secure")}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
